import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Sparkles, Loader2, Lock, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AiMarkdown } from "@/components/AiMarkdown";
import { PublicPage, Seo } from "@/components/public/PublicChrome";
import { LeadCapture } from "@/components/public/LeadCapture";
import { acqParams, trackAcq } from "@/lib/acquisition";
import api, { formatApiError } from "@/lib/api";
import { TOOLS, TOOL_LIST, PROD_ORIGIN } from "@/pages/tools/toolsConfig";

// Injects JSON-LD structured data into <head> and cleans up on unmount.
function JsonLd({ data }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, [data]);
  return null;
}

export default function PublicTool() {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const cfg = TOOLS[paramSlug];

  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [resultId, setResultId] = useState("");
  const [error, setError] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  // Fires tool_start exactly once, on the first meaningful interaction (not on page load).
  const startedRef = useRef(false);

  useEffect(() => {
    setValues({}); setResult(""); setError(""); setRateLimited(false);
    startedRef.current = false;
    if (cfg) {
      trackAcq("tool_view", { tool: cfg.slug });
      try { localStorage.setItem("fv_last_tool", cfg.slug); } catch (e) { console.debug("localStorage unavailable", e); }
    }
  }, [cfg]);

  const signupUrl = useCallback(() => {
    const params = new URLSearchParams(window.location.search); // preserve any UTMs
    params.set("plan", "growth");
    params.set("source", `tool_${cfg.slug}`);
    try {
      const a = acqParams();
      if (a.visitor_id) params.set("vid", a.visitor_id);
    } catch (e) { console.debug("acqParams unavailable", e); }
    if (resultId) params.set("result", resultId);
    return `/register?${params.toString()}`;
  }, [cfg, resultId]);

  const goSignup = (where) => {
    try { localStorage.setItem("fv_tool", cfg.slug); } catch (e) { console.debug("localStorage unavailable", e); }
    if (where === "result") trackAcq("save_result_cta_click", { tool: cfg.slug });
    trackAcq("tool_signup_click", { tool: cfg.slug });
    navigate(signupUrl());
  };

  if (!cfg) {
    return (
      <PublicPage>
        <div className="pt-36 pb-24 px-6 max-w-3xl mx-auto text-center">
          <h1 className="font-heading text-4xl mb-4">Tool not found</h1>
          <Link to="/" className="text-[#D4AF37]">Back to home</Link>
        </div>
      </PublicPage>
    );
  }

  const set = (k, v) => {
    if (!startedRef.current) { startedRef.current = true; trackAcq("tool_start", { tool: cfg.slug }); }
    setValues((s) => ({ ...s, [k]: v }));
  };

  const submit = async () => {
    setError(""); setRateLimited(false);
    const missing = cfg.fields.filter((f) => f.required && !String(values[f.key] || "").trim());
    if (missing.length) { setError(`Please fill in: ${missing.map((f) => f.label.replace(/ \(optional\)/, "")).join(", ")}`); return; }
    setLoading(true);
    try {
      const a = acqParams();
      const { data } = await api.post(`/public/tools/${cfg.slug}`, { inputs: values, visitor_id: a.visitor_id, attribution: a });
      setResult(data.content || "");
      setResultId(data.result_id || "");
      trackAcq("save_result_cta_impression", { tool: cfg.slug });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429 || (detail && detail.code === "public_rate_limited")) {
        setRateLimited(true);
        trackAcq("tool_rate_limited", { tool: cfg.slug });
      } else {
        setError(formatApiError(detail) || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const canonicalPath = `/tools/${cfg.slug}`;
  const url = `${PROD_ORIGIN}${canonicalPath}`;
  const breadcrumb = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${PROD_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Free Business Tools", item: `${PROD_ORIGIN}/tools` },
      { "@type": "ListItem", position: 3, name: cfg.h1, item: url },
    ],
  };
  const software = {
    "@context": "https://schema.org", "@type": "SoftwareApplication",
    name: cfg.software.name, applicationCategory: cfg.software.category,
    operatingSystem: "Web", url,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    provider: { "@type": "Organization", name: "Fairview Systems", url: PROD_ORIGIN },
  };
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: cfg.faqs.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <PublicPage>
      <Seo title={cfg.metaTitle} description={cfg.metaDescription} path={canonicalPath} />
      <JsonLd data={breadcrumb} />
      <JsonLd data={software} />
      <JsonLd data={faqLd} />

      <article className="max-w-4xl mx-auto px-6 pt-32 pb-20">
        {/* Breadcrumb */}
        <nav className="text-xs text-[#71717A] mb-6" data-testid="tool-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-[#A1A1AA]">Free Business Tools</span>
          <span className="mx-2">/</span>
          <span className="text-white">{cfg.keyword}</span>
        </nav>

        {/* Hero */}
        <header className="mb-8">
          <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-3">{cfg.eyebrow}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4" data-testid="tool-h1">{cfg.h1}</h1>
          <p className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl">{cfg.intro}</p>
        </header>

        {cfg.disclaimer && (
          <div className="mb-8 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-4 text-sm text-[#F2E3C6]" data-testid="tool-disclaimer">
            <strong className="text-white">Please note:</strong> {cfg.disclaimer}
          </div>
        )}

        {/* Tool card */}
        <section className="rounded-2xl border border-white/10 bg-[#121214] p-6 sm:p-8 mb-12" data-testid="tool-card">
          <h2 className="font-heading text-2xl mb-5 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" /> {cfg.cardTitle}
          </h2>
          <div className="space-y-4">
            {cfg.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm text-[#D4D4D8] mb-1.5">{f.label}</label>
                {f.type === "textarea" && (
                  <Textarea data-testid={`tool-input-${f.key}`} rows={3} placeholder={f.placeholder || ""}
                    value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                    className="bg-[#0A0A0B] border-white/10 text-white placeholder:text-[#52525B]" />
                )}
                {f.type === "text" && (
                  <Input data-testid={`tool-input-${f.key}`} placeholder={f.placeholder || ""}
                    value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                    className="bg-[#0A0A0B] border-white/10 text-white placeholder:text-[#52525B]" />
                )}
                {f.type === "select" && (
                  <div className="relative">
                    <select data-testid={`tool-input-${f.key}`} value={values[f.key] || ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      className="w-full appearance-none rounded-md bg-[#0A0A0B] border border-white/10 text-white h-10 px-3 pr-9 text-sm">
                      <option value="">Select…</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-[#71717A] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                )}
              </div>
            ))}
            {error && <p className="text-sm text-[#EF4444]" data-testid="tool-error">{error}</p>}
            <Button onClick={submit} disabled={loading} data-testid="tool-submit"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-7 h-11 w-full sm:w-auto">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <>{cfg.submitLabel} <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>

          {/* Rate limited */}
          {rateLimited && (
            <div className="mt-6 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-5 text-center" data-testid="tool-rate-limited">
              <p className="text-sm text-[#F2E3C6] mb-3">You've reached the free limit for our public tools. Create a free account to keep going — it only takes a moment.</p>
              <Button onClick={() => goSignup("rate")} data-testid="tool-rate-signup"
                className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-6">Create free account</Button>
            </div>
          )}

          {/* Result + soft CTA */}
          {result && !rateLimited && (
            <div className="mt-8" data-testid="tool-result">
              <h3 className="font-heading text-xl mb-3">{cfg.resultTitle}</h3>
              <div className="rounded-xl border border-white/10 bg-[#0A0A0B] p-5 text-[#D4D4D8] text-sm leading-relaxed">
                <AiMarkdown text={result} />
              </div>
              <div className="mt-5 rounded-xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1C1C1F] to-[#121214] p-6 text-center" data-testid="tool-cta">
                <Lock className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
                <h4 className="font-heading text-xl mb-1.5 text-white">{cfg.ctaHeadline}</h4>
                <p className="text-sm text-[#A1A1AA] mb-4 max-w-xl mx-auto">{cfg.ctaSub}</p>
                <Button onClick={() => goSignup("result")} data-testid="tool-cta-signup"
                  className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-7 h-11">
                  Save my results &amp; continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-[#71717A] mt-2">Your result is saved to your new account — pick up right where you left off.</p>
              </div>
              <div className="mt-5">
                <LeadCapture tool={cfg.slug} />
              </div>
            </div>
          )}
        </section>

        {/* Supporting content */}
        <section className="prose-fv space-y-10 text-[#A1A1AA]">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-3">What this tool does</h2>
            <p className="text-base leading-relaxed">{cfg.whatItDoes}</p>
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-3">Who it's for</h2>
            <p className="text-base leading-relaxed">{cfg.whoFor}</p>
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-3">How it works</h2>
            <ol className="list-decimal pl-5 space-y-2 text-base">
              {cfg.howItWorks.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-3">What your results mean</h2>
            <p className="text-base leading-relaxed">{cfg.resultsMean}</p>
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-3">Recommended next steps</h2>
            <ul className="list-disc pl-5 space-y-2 text-base">
              {cfg.nextSteps.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-4">Frequently asked questions</h2>
            <div className="space-y-3" data-testid="tool-faq">
              {cfg.faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-white/10 bg-[#121214] p-4" data-testid={`tool-faq-${i}`}>
                  <summary className="cursor-pointer list-none flex items-center justify-between text-white font-medium">
                    {f.q}
                    <ChevronDown className="w-4 h-4 text-[#71717A] transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Related tools */}
        <section className="mt-14 pt-10 border-t border-white/8">
          <h2 className="font-heading text-2xl text-white mb-5">More free business tools</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {TOOL_LIST.filter((t) => t.slug !== cfg.slug).map((t) => (
              <Link key={t.slug} to={`/tools/${t.slug}`} data-testid={`related-${t.slug}`}
                className="rounded-xl border border-white/10 bg-[#121214] p-4 hover:border-[#D4AF37]/40 transition-colors flex items-center justify-between">
                <span className="text-white text-sm">{t.h1}</span>
                <ArrowRight className="w-4 h-4 text-[#D4AF37]" />
              </Link>
            ))}
          </div>
          <Link to="/learn/how-to-start-a-business" data-testid="tool-guide-link"
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:underline">
            New to starting a business? Read our free step-by-step checklist <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </section>
      </article>
    </PublicPage>
  );
}
